import {
  getRelativeDayOrCurrentDateWithTDays,
  todayTomorrowConfig,
  getRegularDate,
  getDateDifference
} from '@utils/dates';
import getItemFromSessionStorage from '@utils/getItemFromSessionStorage';
import isEmpty from '@utils/isEmpty';

export const BATCH_CONTENT_TYPE_ID = 500;

export const BATCH_TYPE = { MODULE: 'Module', PLAYLIST: 'Playlist' };

export const BATCH_CONTENT_TYPE_MAP = {
  LIVE: 'Live',
  RECORDED: 'Recorded',
  LIVE_RECORDED: 'Live + Recorded',
  HYBRID: 'HYBRID',
  CENTER: 'Center',
  OFFLINE: 'Offline'
};

const STORE_BATCH_KEY = 'storeBatchReferralCodes';
export const SEVEN_DAY_FREE_TRIAL = 'seven_day_free_trial_web_experiment';

export const SESSION_TYPE_MAP = {
  ALL: '1',
  CLASSES: '2',
  TESTS: '3'
};

export const LESSON_ITEMS_ON_BATCH_PAGE = {
  START: 0,
  END: 3
};

export const DEFAULT_SUBJECT_VALUE = 'ALL';

export const EDUCATOR_TYPES = {
  PLUS: 1,
  PARTNER: 2
};

export const FETCH_STORE_SYLLABUS_VALUES = {
  LIMIT: 10,
  OFFSET: 0
};

const getIsStorePath = (route) => {
  return route.indexOf('store/batch/') >= 0;
};

const getActiveSku = (purchaseInfo) => {
  const skus = purchaseInfo?.skus;
  return skus?.[0];
};

export const getIsPartnerEducatorBatch = (batchDetails) => {
  return batchDetails?.educatorType === EDUCATOR_TYPES.PARTNER;
};

export const getIsStoreSyllabus = ({ additionalFeatures }) => {
  return additionalFeatures
    ? additionalFeatures?.indexOf(BATCH_TYPE.PLAYLIST) !== -1
    : false;
};

export const getIsStoreModule = ({ additionalFeatures }) => {
  return additionalFeatures
    ? additionalFeatures?.indexOf(BATCH_TYPE.MODULE) !== -1
    : false;
};

const getHasStorePurchase = (batchUID, purchaseInfo) => {
  const purchases = purchaseInfo?.metaInfo?.latestUserSkusTypeWise;

  if (purchases && purchases[BATCH_CONTENT_TYPE_ID]) {
    const batchPurchases = purchases[BATCH_CONTENT_TYPE_ID];
    const currentBatchActivePurchase = batchPurchases.find(
      (purchase) => purchase.objectUid === batchUID
    );

    return !!(
      currentBatchActivePurchase && !isEmpty(currentBatchActivePurchase)
    );
  }

  return false;
};

const getEnrollmentEndMessage = (date) => {
  if (!date) return '';
  const relativeDate = getRelativeDayOrCurrentDateWithTDays(
    date,
    true,
    7,
    true
  );
  const isRelativeDateTodayTomorrow = todayTomorrowConfig.some(
    (day) => day === relativeDate
  );
  if (!relativeDate) {
    return '';
  }
  if (isRelativeDateTodayTomorrow) {
    return `Enrolment ends ${relativeDate}`;
  }
  if (relativeDate <= 7 && relativeDate > 0) {
    return `Enrolment ends in ${relativeDate} days`;
  }
  return `Enrolment ends on ${relativeDate}`;
};

const getOfferEndMessage = (date) => {
  if (!date) return '';
  const relativeDate = getRelativeDayOrCurrentDateWithTDays(
    date,
    true,
    7,
    true
  );
  const isRelativeDateTodayTomorrow = todayTomorrowConfig.some(
    (day) => day === relativeDate
  );
  if (!relativeDate) {
    return '';
  }
  if (isRelativeDateTodayTomorrow) {
    return `Offer ends ${relativeDate}`;
  }
  if (relativeDate <= 7 && relativeDate > 0) {
    return `Offer ends in ${relativeDate} days`;
  }
  return `Offer ends on ${relativeDate}`;
};

const getAccessEndDate = (
  purchaseInfo,
  batchUID,
  isStoreRoute,
  isSubscribed
) => {
  const activeSku = getActiveSku(purchaseInfo);
  if (activeSku && !isEmpty(activeSku) && isStoreRoute && !isSubscribed) {
    return getRegularDate(activeSku?.accessEndDate);
  }

  const purchases = purchaseInfo?.metaInfo?.latestUserSkusTypeWise;

  if (purchases && purchases[BATCH_CONTENT_TYPE_ID]) {
    const batchPurchases = purchases[BATCH_CONTENT_TYPE_ID];
    const currentBatchActivePurchase = batchPurchases.find(
      (purchase) => purchase.objectUid === batchUID
    );

    return (
      currentBatchActivePurchase &&
      getRegularDate(currentBatchActivePurchase.expiredAt)
    );
  }
};

const getBatchType = (contentType) => {
  if (
    contentType === BATCH_CONTENT_TYPE_MAP.LIVE_RECORDED ||
    contentType === BATCH_CONTENT_TYPE_MAP.RECORDED
  ) {
    return 'Recorded';
  }
  return contentType;
};

const getSkuBasedEventProps = (activeSku, batchDetails) => {
  return {
    entity_content_type: 'Batch',
    entity_title: batchDetails?.name,
    entity_uid: batchDetails?.uid,
    last_primary_source: 'Store',
    subscription_type: 'Store Subscription',
    offer_price: activeSku?.price,
    offer_end_date: activeSku?.priceEffectiveTill || undefined,
    subscription_price: activeSku?.maxRetailPrice || activeSku?.price,
    subscription_duration: getDateDifference(activeSku?.accessEndDate),
    entity_content_subtype: getBatchType(batchDetails?.contentType),
    educator_type:
      batchDetails?.educatorType === EDUCATOR_TYPES.PARTNER ? 'Partner' : 'Plus'
  };
};

const getBatchReferralInStorage = (batchUID) => {
  try {
    const referralCodesStr = getItemFromSessionStorage(STORE_BATCH_KEY);

    const referralCodes = !!referralCodesStr && JSON.parse(referralCodesStr);

    return referralCodes && referralCodes[batchUID];
  } catch {
    return null;
  }
};

const setBatchReferralInStorage = (batchUID, code) => {
  try {
    const referralCodesStr = getItemFromSessionStorage(STORE_BATCH_KEY);

    const referralCodes = !!referralCodesStr && JSON.parse(referralCodesStr);
    sessionStorage.setItem(
      STORE_BATCH_KEY,
      JSON.stringify({ ...referralCodes, [batchUID]: code })
    );
  } catch {
    return null;
  }
};

const removeStoreBatchInStorage = () => {
  sessionStorage.removeItem(STORE_BATCH_KEY);
};

export {
  getHasStorePurchase,
  getActiveSku,
  getIsStorePath,
  getEnrollmentEndMessage,
  getOfferEndMessage,
  getAccessEndDate,
  getSkuBasedEventProps,
  setBatchReferralInStorage,
  getBatchReferralInStorage,
  removeStoreBatchInStorage,
  getBatchType
};
